import React, {
  useCallback,
  useEffect,
  useState,
  type ReactNode,
  type MouseEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { PickingInfo, DeckProps } from '@deck.gl/core';
import styled from 'styled-components';
import { notification, Layout } from 'antd';
import { SimViewerSideMenu } from '../components/SimViewer/SimViewerSideMenu/SimViewerSideMenu';
import { Button } from 'antd';
import { DoubleLeftOutlined, DoubleRightOutlined } from '@ant-design/icons';
import { useCurrentSimDataId } from '../hooks/SimViewer/useCurrentSimDataId';
import { useMetaData } from '../hooks/data/useMetaData';
import { useDataFilter } from '../hooks/SimViewer/useDataFilter';
import { useCustomAreaCreation } from '../hooks/SimViewer/useCustomAreaCreation';
import { useMapSelection } from '../hooks/SimViewer/useMapSelection';
import { useCustomAreaEdition } from '../hooks/SimViewer/useCustomAreaEdition';
import { useLoadingStatus } from '../hooks/SimViewer/useLoadingStatus';
import { useCustomAreas } from '../hooks/SimViewer/useCustomAreas';
import { useThreeDMode } from '../hooks/SimViewer/useThreeDMode';
import { useAggregationConfig } from '../hooks/SimViewer/useAggregationConfig';
import { useMapViewStateUpdater } from '../hooks/SimViewer/useMapViewState';
import { useSearchedLocation } from '../hooks/SimViewer/useSearchedLocation';
import { useSchemaComparison } from '../hooks/SimViewer/useSchemaComparison';
import { LoadingMessage } from '../components/LoadingMessage';
import { Header } from '../layouts/Header';
import { Content } from '../layouts/Content';
import { SimDataSwitcher } from '../components/SimViewer/Header/SimDataSwitcher';
import { NewTabButton } from '../components/SimViewer/Header/NewTabButton';
import { ShareButton } from '../components/SimViewer/Header/ShareButton';
import { DownloadButton } from '../components/SimViewer/Header/DownloadButton';
import { StateUrlSynchronizer } from '../components/SimViewer/StateUrlSynchronizer';
import { Map } from '../components/SimViewer/Map';
import { SearchLocation } from '../components/SimViewer/SearchLocation';
import { MultipleSelectingBar } from '../components/MultipleSelectingBar';
import { AreaFilterModal } from '../components/SimViewer/AreaFilterModal';
import { LinkFilterModal } from '../components/SimViewer/LinkFilterModal';

type MjolnirGestureEvent = Parameters<NonNullable<DeckProps['onClick']>>[1];

const MemorizedMap = React.memo(Map);
const MemorizedSimViewerSideMenu = React.memo(SimViewerSideMenu);

const SimViewer = (): ReactNode => {
  const { currentSimDataId } = useCurrentSimDataId();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clearAllFilters } = useDataFilter();
  const { isLoading, currentDataMeta } = useMetaData();
  const { isLoadingEssentials, isLoadingAggregation } = useLoadingStatus();
  const { creatingCustomArea } = useCustomAreaCreation();
  const { editingCustomArea } = useCustomAreaEdition();
  const {
    selectionKind,
    selectedAreaKeyCodes,
    selectedCustomAreaKeys,
    selectedWayIds,
    countOfSelected,
    toggleAreaSelection,
    selectArea,
    toggleCustomAreaSelection,
    selectCustomArea,
    selectLink,
    toggleLinkSelection,
    clearSelection,
  } = useMapSelection();
  const [, contextHolder] = notification.useNotification();
  const { deleteAllCustomAreas } = useCustomAreas();
  const { setThreeDMode } = useThreeDMode();
  const { clearAggregation } = useAggregationConfig();
  const { resetMapViewState } = useMapViewStateUpdater();
  const { setSearchedLocation } = useSearchedLocation();
  const { resetComparisons } = useSchemaComparison();

  const [isAreaFilterModalOpen, setIsAreaFilterModalOpen] =
    useState<boolean>(false);
  const [isLinkFilterModalOpen, setIsLinkFilterModalOpen] =
    useState<boolean>(false);
  const [isSideMenuOpen, setIsSideMenuOpen] = useState<boolean>(true);

  useEffect((): void => {
    if (!currentSimDataId || (!isLoading && !currentDataMeta)) {
      // データIDが未指定の場合、または該当するデータが存在しない場合はトップページへ遷移する
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSimDataId, isLoading]);

  // ページ離脱時に地図の状態をすべてリセットする
  useEffect((): (() => void) => {
    return (): void => {
      clearAllFilters();
      deleteAllCustomAreas();
      clearAggregation();
      setSearchedLocation(undefined);
      resetMapViewState();
      setThreeDMode(false);
      resetComparisons();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect((): (() => void) => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        clearSelection();
        setIsAreaFilterModalOpen(false);
        setIsLinkFilterModalOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return (): void => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [clearSelection, setIsAreaFilterModalOpen, setIsLinkFilterModalOpen]);

  const handleClickMap = useCallback(
    (info: PickingInfo, event: MjolnirGestureEvent): void => {
      if (!info.picked || info.layer == null) {
        return;
      }
      if (event.leftButton) {
        // ポリゴンの作成・編集中は誤操作防止のためにクリックしても何も起こらないようにする
        if (creatingCustomArea || editingCustomArea) {
          return;
        }

        switch (info.layer.id) {
          case 'custom-area-layer':
            // Shiftキーを押しながらクリックした場合は地域を追加するだけ、そうでない場合は地域を追加した上でモーダルを表示する
            if (event.srcEvent.shiftKey) {
              toggleCustomAreaSelection(info.object.key);
            } else {
              selectCustomArea(info.object.key);
              setIsAreaFilterModalOpen(true);
            }
            return;
          case 'area-layer':
          case 'area-city-layer':
            // Shiftキーを押しながらクリックした場合はエリアを追加するだけ、そうでない場合はエリアを追加した上でモーダルを表示する
            if (event.srcEvent.shiftKey) {
              toggleAreaSelection(info.object.key_code);
            } else {
              selectArea(info.object.key_code);
              setIsAreaFilterModalOpen(true);
            }
            return;
          case 'link-layer':
            // Shiftキーを押しながらクリックした場合はリンクを追加するだけ、そうでない場合はリンクを追加した上でモーダルを表示する
            if (event.srcEvent.shiftKey) {
              toggleLinkSelection(info.object.way_id);
            } else {
              selectLink(info.object.way_id);
              setIsLinkFilterModalOpen(true);
            }
            return;
        }
      }
    },
    [
      creatingCustomArea,
      editingCustomArea,
      toggleAreaSelection,
      selectArea,
      toggleCustomAreaSelection,
      selectCustomArea,
      setIsAreaFilterModalOpen,
      selectLink,
      toggleLinkSelection,
      setIsLinkFilterModalOpen,
    ]
  );

  if (!currentSimDataId || !currentDataMeta) {
    return null;
  }
  return (
    <Layout>
      <Header
        items={{
          left: <SimDataSwitcher key={currentSimDataId} />,
          right: [
            <NewTabButton key="new-tab-button" />,
            <ShareButton key="share-button" />,
            <DownloadButton key="download-button" />,
          ],
        }}
      />
      <Content>
        {!isAreaFilterModalOpen &&
          !isLinkFilterModalOpen &&
          selectionKind !== 'none' && (
            <MultipleSelectingBar
              type={selectionKind}
              count={countOfSelected}
              onConfirm={(): void => {
                if (selectionKind === 'area') {
                  setIsAreaFilterModalOpen(true);
                } else {
                  setIsLinkFilterModalOpen(true);
                }
              }}
              onDiscard={clearSelection}
            />
          )}
        {contextHolder}
        <FullScreenContainer
          data-side-menu-open={isSideMenuOpen}
          onContextMenu={(event: MouseEvent): void => {
            // 右クリックでのコンテキストメニューを無効化する
            event.preventDefault();
          }}
        >
          <StateUrlSynchronizer
            initialViewState={currentDataMeta.initial_view_state}
          >
            <MemorizedMap handleClickMap={handleClickMap} />
          </StateUrlSynchronizer>
          <SideMenuButton
            icon={
              isSideMenuOpen ? <DoubleLeftOutlined /> : <DoubleRightOutlined />
            }
            data-side-menu-open={isSideMenuOpen}
            onClick={(): void => {
              setIsSideMenuOpen(!isSideMenuOpen);
            }}
          />
          <SearchForm data-side-menu-open={isSideMenuOpen}>
            <SearchLocation />
          </SearchForm>

          <MemorizedSimViewerSideMenu open={isSideMenuOpen} />
          {(isLoadingEssentials || isLoadingAggregation) && (
            <LoadingMessage message={t('loadingNow')} />
          )}
        </FullScreenContainer>
        <AreaFilterModal
          key={`${selectedAreaKeyCodes.join(',')}-${selectedCustomAreaKeys.join(
            ','
          )}`}
          areaKeyCodes={selectedAreaKeyCodes}
          customAreaKeys={selectedCustomAreaKeys}
          isOpen={isAreaFilterModalOpen && selectionKind === 'area'}
          onClose={(): void => {
            clearSelection();
            setIsAreaFilterModalOpen(false);
          }}
        />
        <LinkFilterModal
          key={selectedWayIds.join(',')}
          wayIds={selectedWayIds}
          isOpen={isLinkFilterModalOpen && selectionKind === 'link'}
          onClose={(): void => {
            clearSelection();
            setIsLinkFilterModalOpen(false);
          }}
        />
      </Content>
    </Layout>
  );
};

const FullScreenContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;

  &[data-side-menu-open='true'] {
    .deck-widget-locator:not(.deck-widget-locator .deck-widget-locator),
    .deck-widget-compass:not(.deck-widget-compass .deck-widget-compass),
    .deck-widget-scale-bar:not(.deck-widget-scale-bar .deck-widget-scale-bar) {
      transform: translateX(clamp(360px, 25vw, 421px));
    }
  }
`;

const SideMenuButton = styled(Button)`
  position: absolute;
  z-index: 1;
  top: 20px;
  left: 16px;
  transition: left 0.24s ease-in-out;
  &[data-side-menu-open='true'] {
    transform: translateX(clamp(360px, 25vw, 421px));
  }
`;

const SearchForm = styled.div`
  position: absolute;
  z-index: 1;
  top: 16px;
  left: 60px;
  &[data-side-menu-open='true'] {
    transform: translateX(clamp(360px, 25vw, 421px));
  }

  border-radius: 4px;
  background-color: #ffffff;
`;

export { SimViewer };
